import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

/**
 * Spike 1 polish §4 — iOS keyboard scroll-into-view.
 *
 * On iOS Safari, when a virtual keyboard appears the layout viewport stays
 * the same but the *visual viewport* shrinks. Native inputs get scrolled
 * into view automatically; embedded `contentEditable` (which is how Lexical
 * surfaces its text) does not always get the same treatment, especially
 * when nested in a flex container with `overflow: auto`. The user types
 * and the cursor disappears behind the keyboard.
 *
 * Fix: subscribe to the editor's selection/focus events and, when the
 * caret is below the visualViewport's offsetTop + height, scroll the
 * caret into view with a small bottom-padding so it sits above the
 * keyboard line.
 *
 * No-op outside iOS-ish environments (no visualViewport) — the desktop
 * shell never triggers the keyboard.
 */
export const KeyboardScrollIntoViewPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return; // older browsers / non-iOS may not expose visualViewport.

    let raf = 0;

    const ensureCaretVisible = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0).cloneRange();
        // Inject a zero-width marker to get coordinates even when the
        // selection is collapsed at a line edge (rect width 0 still has
        // a position).
        const rects = range.getClientRects();
        const rect = rects.length > 0 ? rects[0] : range.getBoundingClientRect();
        const caretBottom = rect.bottom;

        // visualViewport.offsetTop + height = the bottom of the visible
        // viewport in document coordinates.
        const viewportBottom = vv.offsetTop + vv.height;
        const padding = 24; // small breathing room above the keyboard.

        if (caretBottom > viewportBottom - padding) {
          const delta = caretBottom - (viewportBottom - padding);
          window.scrollBy({ top: delta, behavior: 'smooth' });
        }
      });
    };

    // Selection changes inside Lexical re-emit through this listener — keeps
    // the caret visible while the user is typing or arrow-keying around.
    const unregister = editor.registerUpdateListener(({ tags }) => {
      // Tag 'history-merge' / similar — we don't need to react to those.
      if (tags.has('historic')) return;
      ensureCaretVisible();
    });

    // Also re-check when the visual viewport itself changes height — the
    // keyboard opening / closing fires this even without a selection change.
    const onVvChange = () => ensureCaretVisible();
    vv.addEventListener('resize', onVvChange);
    vv.addEventListener('scroll', onVvChange);

    return () => {
      unregister();
      vv.removeEventListener('resize', onVvChange);
      vv.removeEventListener('scroll', onVvChange);
      cancelAnimationFrame(raf);
    };
  }, [editor]);

  return null;
};
